import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);
const CookieBanner = lazy(() => import("./components/CookieBanner"));
const Integritetspolicy = lazy(() => import("./pages/Integritetspolicy"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Sortiment = lazy(() => import("./pages/Sortiment"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VaruautomatStad = lazy(() => import("./pages/VaruautomatStad"));
const LundLandingPage = lazy(() => import("./pages/LundLandingPage"));
const LandskronaLandingPage = lazy(() => import("./pages/LandskronaLandingPage"));
const HelsingborgLandingPage = lazy(() => import("./pages/HelsingborgLandingPage"));
const LommaLandingPage = lazy(() => import("./pages/LommaLandingPage"));
const AutomatModell = lazy(() => import("./pages/AutomatModell"));

const queryClient = new QueryClient();

/**
 * URL-normalisering. Hostingen serverar index.html för både /path och /path/
 * (och för dubbla snedstreck), vilket annars ger två URL:er med samma innehåll.
 * Vi städar bort trailing slash och dubbletter av "/" innan rutterna matchas,
 * så att bara den kanoniska varianten någonsin renderas.
 */
const NormalizeUrl = () => {
  const { pathname, search, hash } = useLocation();
  const clean = pathname.replace(/\/{2,}/g, "/").replace(/(.+?)\/+$/, "$1");
  if (clean !== pathname) return <Navigate to={`${clean}${search}${hash}`} replace />;
  return null;
};

/**
 * Vid byte av sida (utan hash) ska besökaren alltid börja högst upp.
 * React Router återställer inte scrollposition automatiskt, så länkar i
 * footern skulle annars öppna den nya sidan halvvägs ner.
 */
const ScrollReset = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <NormalizeUrl />
        <ScrollReset />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/integritetspolicy" element={<Integritetspolicy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/sortiment" element={<Sortiment />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/varuautomat-lund" element={<LundLandingPage />} />
            <Route path="/varuautomat-landskrona" element={<LandskronaLandingPage />} />
            <Route path="/varuautomat-helsingborg" element={<HelsingborgLandingPage />} />
            <Route path="/varuautomat-lomma" element={<LommaLandingPage />} />
            {["malmo", "trelleborg", "vellinge", "eslov", "skurup", "skane"].map((slug) => (
              <Route
                key={slug}
                path={`/varuautomat-${slug}`}
                element={<VaruautomatStad />}
              />
            ))}
            {["kompakt-varuautomat", "classic-varuautomat", "premium-varuautomat"].map((slug) => (
              <Route key={slug} path={`/automater/${slug}`} element={<AutomatModell />} />
            ))}
            <Route path="/om-oss" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
